/**
 * Pod geometry helpers — no map-registry / campus-layouts imports
 * (breaks circular init with campus builders).
 */

export const MAP_COLS = 54;
export const MAP_ROWS = 42;

export type DoorWall = "N" | "S" | "E" | "W";

export type PersonalOfficeSlotLayout = {
  slotKey: string;
  nameDefault: string;
  /** Inclusive zone bounds (walls included). */
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Walkable door tile on the wall facing the center. */
  door: { gx: number; gy: number };
  doorWall: DoorWall;
  /** Interior chair / sit target. */
  interiorSeat: { gx: number; gy: number };
  /**
   * Which way the sitter faces (toward the desk).
   * N/S/E/W — used by the world engine for chair draw + avatar sit facing.
   */
  seatDir: DoorWall;
  /** Desk (1×1) so the door aisle stays open. */
  desk: { gx: number; gy: number };
  /** Which way the desk / monitor faces (toward the seat). */
  deskDir: DoorWall;
  /**
   * Fixed decor anchor tiles (floor perks — not wall art).
   * Phase 3: up to 4 spots so multiple unlocked perks can sit in a pod.
   */
  decorSlots: ReadonlyArray<{ gx: number; gy: number }>;
  /**
   * Interior tile for hanging wall photos — on the back wall (opposite the door),
   * never on the desk/seat so frames aren't buried under furniture.
   * Prefer `wallArtSlots[0]` when present; kept for older call sites.
   */
  wallArt: { gx: number; gy: number };
  /**
   * Back-wall photo anchors (primary + optional secondary).
   * Index 0 matches `wallArt`.
   */
  wallArtSlots: ReadonlyArray<{ gx: number; gy: number }>;
};

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Derive door / seat / desk / decor anchors for an office pod from bounds + door wall.
 * Used by the default layout builder and the admin map editor.
 */
export function makePodFromBounds(opts: {
  slotKey: string;
  nameDefault: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  doorWall: DoorWall;
}): PersonalOfficeSlotLayout {
  const { slotKey, nameDefault, x1, y1, x2, y2, doorWall } = opts;
  const midX = Math.floor((x1 + x2) / 2);
  const midY = Math.floor((y1 + y2) / 2);
  // Keep furniture strictly inside walls (not on perimeter).
  const ix1 = x1 + 1;
  const iy1 = y1 + 1;
  const ix2 = x2 - 1;
  const iy2 = y2 - 1;

  let door: { gx: number; gy: number };
  let seat: { gx: number; gy: number };
  let desk: { gx: number; gy: number };
  let decor: { gx: number; gy: number };
  let seatDir: DoorWall;
  let deskDir: DoorWall;
  let wallArt: { gx: number; gy: number };

  // Wall art on the back wall; seat faces the desk; desk sits in front of the seat
  // so people look into the room / toward the door aisle.
  let decorSlots: Array<{ gx: number; gy: number }>;
  let wallArtSlots: Array<{ gx: number; gy: number }>;

  if (doorWall === "S") {
    // North-wall pod — door faces south into HQ
    door = { gx: midX, gy: y2 };
    wallArt = { gx: midX, gy: iy1 };
    wallArtSlots = [
      { gx: midX, gy: iy1 },
      { gx: clamp(midX + 1, ix1, ix2), gy: iy1 },
    ];
    seat = { gx: midX, gy: clamp(iy1 + 1, iy1, iy2) };
    desk = { gx: midX, gy: clamp(iy1 + 2, iy1, iy2) };
    decor = { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy1 + 1, iy1, iy2) };
    decorSlots = [
      { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy1 + 1, iy1, iy2) },
      { gx: clamp(midX - 1, ix1, ix2), gy: clamp(iy1 + 1, iy1, iy2) },
      { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy1 + 2, iy1, iy2) },
      { gx: clamp(midX - 1, ix1, ix2), gy: clamp(iy1 + 2, iy1, iy2) },
    ];
    seatDir = "S";
    deskDir = "N";
  } else if (doorWall === "N") {
    // South-wall pod — door faces north
    door = { gx: midX, gy: y1 };
    wallArt = { gx: midX, gy: iy2 };
    wallArtSlots = [
      { gx: midX, gy: iy2 },
      { gx: clamp(midX - 1, ix1, ix2), gy: iy2 },
    ];
    seat = { gx: midX, gy: clamp(iy2 - 1, iy1, iy2) };
    desk = { gx: midX, gy: clamp(iy2 - 2, iy1, iy2) };
    decor = { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy2 - 1, iy1, iy2) };
    decorSlots = [
      { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy2 - 1, iy1, iy2) },
      { gx: clamp(midX - 1, ix1, ix2), gy: clamp(iy2 - 1, iy1, iy2) },
      { gx: clamp(midX + 1, ix1, ix2), gy: clamp(iy2 - 2, iy1, iy2) },
      { gx: clamp(midX - 1, ix1, ix2), gy: clamp(iy2 - 2, iy1, iy2) },
    ];
    seatDir = "N";
    deskDir = "S";
  } else if (doorWall === "E") {
    // West-wall pod — door faces east
    door = { gx: x2, gy: midY };
    wallArt = { gx: ix1, gy: midY };
    wallArtSlots = [
      { gx: ix1, gy: midY },
      { gx: ix1, gy: clamp(midY + 1, iy1, iy2) },
    ];
    seat = { gx: clamp(ix1 + 1, ix1, ix2), gy: midY };
    desk = { gx: clamp(ix1 + 2, ix1, ix2), gy: midY };
    decor = { gx: clamp(ix1 + 1, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) };
    decorSlots = [
      { gx: clamp(ix1 + 1, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) },
      { gx: clamp(ix1 + 1, ix1, ix2), gy: clamp(midY - 1, iy1, iy2) },
      { gx: clamp(ix1 + 2, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) },
      { gx: clamp(ix1 + 2, ix1, ix2), gy: clamp(midY - 1, iy1, iy2) },
    ];
    seatDir = "E";
    deskDir = "W";
  } else {
    // East-wall pod — door faces west
    door = { gx: x1, gy: midY };
    wallArt = { gx: ix2, gy: midY };
    wallArtSlots = [
      { gx: ix2, gy: midY },
      { gx: ix2, gy: clamp(midY - 1, iy1, iy2) },
    ];
    seat = { gx: clamp(ix2 - 1, ix1, ix2), gy: midY };
    desk = { gx: clamp(ix2 - 2, ix1, ix2), gy: midY };
    decor = { gx: clamp(ix2 - 1, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) };
    decorSlots = [
      { gx: clamp(ix2 - 1, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) },
      { gx: clamp(ix2 - 1, ix1, ix2), gy: clamp(midY - 1, iy1, iy2) },
      { gx: clamp(ix2 - 2, ix1, ix2), gy: clamp(midY + 1, iy1, iy2) },
      { gx: clamp(ix2 - 2, ix1, ix2), gy: clamp(midY - 1, iy1, iy2) },
    ];
    seatDir = "W";
    deskDir = "E";
  }

  if (desk.gx === seat.gx && desk.gy === seat.gy) {
    // Tiny interior — nudge desk one step in seatDir
    if (seatDir === "S") desk = { gx: seat.gx, gy: clamp(seat.gy + 1, iy1, iy2) };
    else if (seatDir === "N") desk = { gx: seat.gx, gy: clamp(seat.gy - 1, iy1, iy2) };
    else if (seatDir === "E") desk = { gx: clamp(seat.gx + 1, ix1, ix2), gy: seat.gy };
    else desk = { gx: clamp(seat.gx - 1, ix1, ix2), gy: seat.gy };
  }
  // Drop decor slots that land on seat/desk or duplicate positions.
  const blocked = new Set([`${seat.gx},${seat.gy}`, `${desk.gx},${desk.gy}`]);
  const uniqueDecor: Array<{ gx: number; gy: number }> = [];
  for (const d of decorSlots) {
    const key = `${d.gx},${d.gy}`;
    if (blocked.has(key)) continue;
    if (uniqueDecor.some((u) => u.gx === d.gx && u.gy === d.gy)) continue;
    uniqueDecor.push(d);
    if (uniqueDecor.length >= 4) break;
  }
  if (uniqueDecor.length === 0) {
    uniqueDecor.push({
      gx: clamp(seat.gx + 1, ix1, ix2),
      gy: clamp(seat.gy + 1, iy1, iy2),
    });
  }
  decor = uniqueDecor[0] ?? decor;

  if (
    (wallArt.gx === seat.gx && wallArt.gy === seat.gy) ||
    (wallArt.gx === desk.gx && wallArt.gy === desk.gy)
  ) {
    // Prefer keeping wall art on the back wall; shift seat instead
    if (doorWall === "S") seat = { gx: seat.gx, gy: clamp(seat.gy + 1, iy1, iy2) };
    else if (doorWall === "N") seat = { gx: seat.gx, gy: clamp(seat.gy - 1, iy1, iy2) };
    else if (doorWall === "E") seat = { gx: clamp(seat.gx + 1, ix1, ix2), gy: seat.gy };
    else seat = { gx: clamp(seat.gx - 1, ix1, ix2), gy: seat.gy };
  }

  // Ensure wallArtSlots[0] matches wallArt; de-dupe secondary
  wallArtSlots = [
    wallArt,
    ...wallArtSlots.filter((w) => !(w.gx === wallArt.gx && w.gy === wallArt.gy)),
  ].slice(0, 2);

  return {
    slotKey,
    nameDefault,
    x1,
    y1,
    x2,
    y2,
    door,
    doorWall,
    interiorSeat: seat,
    seatDir,
    desk,
    deskDir,
    decorSlots: uniqueDecor,
    wallArt,
    wallArtSlots,
  };
}
